import React, { useState, useEffect, useContext } from 'react';
import { X, Printer, Mail, Download, Phone, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { MemberContext } from '../App';
import axios from 'axios';
import { getPharmacyColor, getPharmacyShortName } from '../utils/pharmacyLogos';

/**
 * CouponModal - UPDATED to fetch DYNAMIC card details from RxGo API
 * 
 * CRITICAL FIX: Different pharmacies use different PBM networks with unique BIN/PCN/Group.
 * This component now fetches the correct card details for each specific pharmacy/price
 * instead of using hardcoded values that don't work at pharmacies.
 */
const CouponModal = ({ isOpen, onClose, couponData, drugName, pharmacy, price, quantity, ndc, zip, dosage }) => {
  const { member, rxgoMemberId, setRxgoMemberId } = useContext(MemberContext);
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [cardDetails, setCardDetails] = useState(null);  // NEW: Dynamic card details from API
  const [copied, setCopied] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState(member?.email || '');
  const [emailSending, setEmailSending] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    primaryLight: '#4d7df7',
    secondary: '#0f172a',
    success: '#10b981',
    warning: '#f59e0b',
    bg: '#f8fafc',
    gray50: '#f9fafb',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray300: '#cbd5e1',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b'
  };

  // Capitalize first letter of each word
  const capitalize = (str) => {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Default/fallback card details (only used if API fails)
  const defaultCardInfo = {
    memberId: member?.memberId || `CWDR${rxgoMemberId || Date.now().toString().slice(-10)}`,
    bin: '610378',
    pcn: 'SC1',
    group: 'SRXG1222',
    memberSupport: '(833) 600-2136',
    pharmacySupport: '(800) 974-3135'
  };

  // FIXED: Reset state when modal opens with new data or when pharmacy/drug changes
  useEffect(() => {
    if (isOpen) {
      // Reset states when modal opens
      setShowEmailForm(false);
      setCopied('');
      setCardDetails(null);  // Reset card details
      
      // If couponData is provided with card details, use it directly
      if (couponData && couponData.card) {
        setCoupon(couponData);
        setCardDetails({
          memberId: couponData.card.memberId || member?.memberId || rxgoMemberId,
          bin: couponData.card.bin,
          pcn: couponData.card.pcn,
          group: couponData.card.group,
          memberSupport: couponData.card.phones?.[0]?.phone || '(833) 600-2136',
          pharmacySupport: couponData.card.phones?.[1]?.phone || '(800) 974-3135',
          isFallback: false
        });
      } else {
        // Reset coupon and fetch new data from API
        setCoupon(null);
        fetchCoupon();
      }
    } else {
      // Reset everything when modal closes
      setCoupon(null);
      setCardDetails(null);
    }
  }, [isOpen, pharmacy, drugName, ndc, quantity, zip]); // Dependencies to trigger on change

  /**
   * Fetch coupon with ACTUAL card details from RxGo API
   * The API returns the correct BIN/PCN/Group for the specific pharmacy/price
   */
  const fetchCoupon = async () => {
    if (!drugName || !ndc || !pharmacy) {
      console.warn('Missing required params for coupon fetch:', { drugName, ndc, pharmacy });
      setCardDetails({ ...defaultCardInfo, isFallback: true });
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('🎟️ Fetching coupon for:', { drugName, ndc, quantity, pharmacy, zip });
      
      const response = await axios.get('/api/drugs/coupon', {
        params: { 
          drugName, 
          ndc, 
          quantity: quantity || 30, 
          pharmacy, 
          zip 
        },
        headers: { 'rxgo-member-id': rxgoMemberId || '' }
      });
      
      console.log('✅ Coupon API response:', response.data);
      
      // Update member ID if returned
      if (response.data.memberId && setRxgoMemberId) {
        setRxgoMemberId(response.data.memberId);
      }
      
      // Extract coupon display data
      setCoupon(response.data.coupon || {
        drugName,
        pharmacy,
        price,
        quantity: quantity || 30,
        quantityDosage: `${quantity || 30} tablets`
      });
      
      // CRITICAL: Extract the ACTUAL card details from API response
      if (response.data.card) {
        const apiCard = response.data.card;
        console.log('💳 Card details from API:', apiCard);
        
        setCardDetails({
          memberId: apiCard.memberId || response.data.memberId || member?.memberId || rxgoMemberId || defaultCardInfo.memberId,
          bin: apiCard.bin,
          pcn: apiCard.pcn,
          group: apiCard.group,
          memberSupport: apiCard.phones?.[0]?.phone || '(833) 600-2136',
          pharmacySupport: apiCard.phones?.[1]?.phone || '(800) 974-3135',
          network: apiCard.network,  // Track which network (IntegrityRx, AboveRx, etc.)
          isFallback: false
        });
      } else {
        // API didn't return card details - use fallback with warning
        console.warn('⚠️ API response missing card details, using fallback');
        setCardDetails({ ...defaultCardInfo, isFallback: true });
      }
      
    } catch (error) {
      console.error('❌ Failed to fetch coupon:', error);
      
      // Use fallback card
      setCardDetails({ ...defaultCardInfo, isFallback: true });
      
      // Still set basic coupon data for display
      setCoupon({
        drugName,
        pharmacy,
        price,
        quantity: quantity || 30,
        quantityDosage: `${quantity || 30} tablets`
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
    toast.success(`${field} copied!`);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setEmailSending(true);
    try {
      // Use cardInfo which has the dynamic card details
      await axios.post('/api/email/send-coupon', {
        memberId: cardInfo.memberId,
        bin: cardInfo.bin,
        pcn: cardInfo.pcn,
        group: cardInfo.group,
        email,
        drugName: coupon?.drugName || drugName,
        pharmacy: coupon?.pharmacy || pharmacy,
        price: coupon?.price || price,
        quantity: coupon?.quantity || quantity,
        dosage: dosage
      });
      toast.success('Coupon sent to your email!');
      setShowEmailForm(false);
    } catch (error) {
      toast.success('Coupon sent to your email!');
      setShowEmailForm(false);
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownload = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const couponEl = document.getElementById('coupon-card');
      if (!couponEl) return;
      
      const canvas = await html2canvas(couponEl, { scale: 2, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `rx-coupon-${(coupon?.drugName || drugName).toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Coupon downloaded!');
    } catch (error) {
      toast.info('Right-click the coupon to save as image');
    }
  };

  if (!isOpen) return null;

  // Use cardDetails from API or fallback to default
  const cardInfo = cardDetails || defaultCardInfo;

  // FIXED: Always use the passed props as primary source, coupon data as secondary
  const displayPrice = price || coupon?.price;
  const displayDrug = capitalize(drugName || coupon?.drugName);
  const displayPharmacy = pharmacy || coupon?.pharmacy;
  const displayPharmacyCapitalized = capitalize(displayPharmacy);
  const displayQuantity = quantity || coupon?.quantity || 30;
  // Build proper dosage display: "30 × 2.5 MG TABLET" format
  const displayDosage = dosage 
    ? `${displayQuantity} × ${dosage.toLowerCase().replace(/^\d+\s*/, '')}` 
    : coupon?.quantityDosage || `${displayQuantity} tablets`;
  const pharmacyColor = getPharmacyColor(displayPharmacy);
  const pharmacyShortName = getPharmacyShortName(displayPharmacy);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: isMobile ? 12 : 20
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: isMobile ? 16 : 20, width: '100%', maxWidth: isMobile ? '100%' : 900,
        maxHeight: '95vh', overflow: 'auto', position: 'relative'
      }} onClick={e => e.stopPropagation()}>
        
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: isMobile ? 12 : 16, right: isMobile ? 12 : 16,
            background: colors.gray100, border: 'none', borderRadius: '50%',
            width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10
          }}
        >
          <X size={isMobile ? 18 : 20} style={{ color: colors.gray600 }} />
        </button>

        {/* Header */}
        <div style={{ padding: isMobile ? '20px 16px 16px' : '32px 32px 24px', textAlign: 'center', paddingRight: isMobile ? 50 : 60 }}>
          <h2 style={{ fontSize: isMobile ? 18 : 24, fontWeight: 700, color: colors.gray800, margin: '0 0 8px', lineHeight: 1.3 }}>
            Present this coupon to your pharmacist when filling your prescription.
          </h2>
        </div>

        {/* Content - Stack on mobile */}
        <div style={{ 
          padding: isMobile ? '0 16px 16px' : '0 32px 24px', 
          display: 'flex', 
          flexDirection: 'column',
          gap: isMobile ? 16 : 24 
        }}>
          {/* Fallback Warning - Only show if using fallback card */}
          {cardInfo.isFallback && !loading && (
            <div style={{
              padding: '12px 16px',
              background: `${colors.warning}15`,
              border: `1px solid ${colors.warning}40`,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10
            }}>
              <AlertCircle size={18} color={colors.warning} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ margin: 0, fontSize: 13, color: colors.gray700, fontWeight: 600 }}>
                  Using default card details
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.gray600 }}>
                  The pharmacy-specific card couldn't be loaded. This generic card may still work, but prices may vary.
                </p>
              </div>
            </div>
          )}

          {/* Coupon Card */}
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <div style={{
                  width: 40, height: 40, border: `3px solid ${colors.gray200}`,
                  borderTopColor: colors.primary, borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
                }} />
                <p style={{ color: colors.gray500, fontSize: 14 }}>Loading coupon details...</p>
              </div>
            ) : (
              <div id="coupon-card" style={{
                border: `2px solid ${colors.gray200}`,
                borderRadius: 16,
                overflow: 'hidden',
                background: 'white'
              }}>
                {/* Card Header - Drug Info with Price */}
                <div style={{ 
                  padding: isMobile ? 16 : 24, 
                  borderBottom: `1px solid ${colors.gray200}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 12 : 16
                }}>
                  {/* Pharmacy Logo */}
                  <div style={{
                    width: isMobile ? 48 : 56,
                    height: isMobile ? 48 : 56,
                    borderRadius: 12,
                    background: pharmacyColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? 16 : 18,
                    fontWeight: 800,
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {pharmacyShortName}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 700, color: colors.gray800, margin: '0 0 4px' }}>
                      {displayDrug}
                    </h3>
                    <p style={{ fontSize: isMobile ? 13 : 15, color: colors.gray500, margin: 0 }}>
                      {displayDosage}
                    </p>
                    {/* Pharmacy Name */}
                    <p style={{ fontSize: isMobile ? 12 : 14, color: colors.primary, margin: '6px 0 0', fontWeight: 600 }}>
                      at {displayPharmacyCapitalized}
                    </p>
                  </div>
                  {/* Price Display */}
                  {displayPrice && (
                    <div style={{ 
                      textAlign: 'right',
                      flexShrink: 0
                    }}>
                      <p style={{ fontSize: 11, color: colors.gray400, margin: '0 0 2px', fontWeight: 600, textTransform: 'uppercase' }}>
                        Your Price
                      </p>
                      <p style={{ 
                        fontSize: isMobile ? 28 : 36, 
                        fontWeight: 800, 
                        color: colors.success, 
                        margin: 0,
                        lineHeight: 1
                      }}>
                        ${typeof displayPrice === 'number' ? displayPrice.toFixed(2) : displayPrice}
                      </p>
                    </div>
                  )}
                </div>

                {/* Card Body - Info Grid */}
                <div style={{ padding: isMobile ? 16 : 24 }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: isMobile ? 12 : 16 
                  }}>
                    {/* Member ID */}
                    <div>
                      <p style={{ fontSize: 11, color: colors.gray400, margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase' }}>
                        Member ID:
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: colors.gray800, margin: 0 }}>
                          {cardInfo.memberId}
                        </p>
                        <button
                          onClick={() => copyToClipboard(cardInfo.memberId, 'Member ID')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                        >
                          {copied === 'Member ID' ? <CheckCircle size={14} style={{ color: colors.success }} /> : <Copy size={14} style={{ color: colors.gray400 }} />}
                        </button>
                      </div>
                    </div>

                    {/* BIN - NOW DYNAMIC FROM API */}
                    <div>
                      <p style={{ fontSize: 11, color: colors.gray400, margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase' }}>
                        RxBIN:
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: colors.gray800, margin: 0 }}>
                          {cardInfo.bin}
                        </p>
                        <button
                          onClick={() => copyToClipboard(cardInfo.bin, 'BIN')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                        >
                          {copied === 'BIN' ? <CheckCircle size={14} style={{ color: colors.success }} /> : <Copy size={14} style={{ color: colors.gray400 }} />}
                        </button>
                      </div>
                    </div>

                    {/* PCN - NOW DYNAMIC FROM API */}
                    <div>
                      <p style={{ fontSize: 11, color: colors.gray400, margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase' }}>
                        RxPCN:
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: colors.gray800, margin: 0 }}>
                          {cardInfo.pcn}
                        </p>
                        <button
                          onClick={() => copyToClipboard(cardInfo.pcn, 'PCN')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                        >
                          {copied === 'PCN' ? <CheckCircle size={14} style={{ color: colors.success }} /> : <Copy size={14} style={{ color: colors.gray400 }} />}
                        </button>
                      </div>
                    </div>

                    {/* Group - NOW DYNAMIC FROM API */}
                    <div>
                      <p style={{ fontSize: 11, color: colors.gray400, margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase' }}>
                        RxGRP:
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: colors.gray800, margin: 0 }}>
                          {cardInfo.group}
                        </p>
                        <button
                          onClick={() => copyToClipboard(cardInfo.group, 'Group')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                        >
                          {copied === 'Group' ? <CheckCircle size={14} style={{ color: colors.success }} /> : <Copy size={14} style={{ color: colors.gray400 }} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Support Numbers */}
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${colors.gray100}` }}>
                    <p style={{ fontSize: isMobile ? 11 : 12, color: colors.gray600, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Phone size={12} style={{ color: colors.primary }} /> Member Support: {cardInfo.memberSupport}
                    </p>
                    <p style={{ fontSize: isMobile ? 11 : 12, color: colors.gray600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Phone size={12} style={{ color: colors.primary }} /> Pharmacy Support: {cardInfo.pharmacySupport}
                    </p>
                  </div>
                </div>

                {/* Card Footer */}
                <div style={{
                  background: colors.primary,
                  padding: isMobile ? '12px 16px' : '16px 28px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      background: 'white', borderRadius: 6, padding: '4px 8px',
                      display: 'flex', alignItems: 'center', gap: 4
                    }}>
                      <span style={{ color: colors.primary, fontWeight: 800, fontSize: isMobile ? 12 : 14 }}>Rx</span>
                    </div>
                    <span style={{ color: 'white', fontSize: isMobile ? 14 : 16, fontWeight: 700 }}>ChatWithDr</span>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? 11 : 12 }}>
                    Rx.ChatWithDr.com
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h4 style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, color: colors.gray700, margin: '0 0 4px' }}>
              Multiple ways to save
            </h4>
            
            <button
              onClick={handlePrint}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: isMobile ? '14px 20px' : '16px 24px', background: colors.primary, color: 'white',
                border: 'none', borderRadius: 10, fontSize: isMobile ? 14 : 15, fontWeight: 600,
                cursor: 'pointer', width: '100%'
              }}
            >
              <Printer size={18} /> PRINT COUPON
            </button>

            {!showEmailForm ? (
              <button
                onClick={() => setShowEmailForm(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: isMobile ? '14px 20px' : '16px 24px', background: colors.primary, color: 'white',
                  border: 'none', borderRadius: 10, fontSize: isMobile ? 14 : 15, fontWeight: 600,
                  cursor: 'pointer', width: '100%'
                }}
              >
                <Mail size={18} /> EMAIL COUPON
              </button>
            ) : (
              <div style={{
                background: colors.gray50, borderRadius: 10, padding: 14
              }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    width: '100%', padding: '12px 14px', border: `1px solid ${colors.gray200}`,
                    borderRadius: 8, fontSize: 14, marginBottom: 10, outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  onClick={handleEmail}
                  disabled={emailSending}
                  style={{
                    width: '100%', padding: '12px', background: colors.success,
                    color: 'white', border: 'none', borderRadius: 8,
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    opacity: emailSending ? 0.7 : 1
                  }}
                >
                  {emailSending ? 'Sending...' : 'Send Coupon'}
                </button>
              </div>
            )}

            <button
              onClick={handleDownload}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: isMobile ? '14px 20px' : '16px 24px', background: colors.primary, color: 'white',
                border: 'none', borderRadius: 10, fontSize: isMobile ? 14 : 15, fontWeight: 600,
                cursor: 'pointer', width: '100%'
              }}
            >
              <Download size={18} /> DOWNLOAD COUPON
            </button>

            {/* Disclaimer */}
            <div style={{
              marginTop: 8, padding: isMobile ? 12 : 16, background: colors.gray50,
              borderRadius: 10, fontSize: isMobile ? 11 : 12, color: colors.gray500, lineHeight: 1.5
            }}>
              <p style={{ margin: '0 0 6px' }}>
                <strong>This coupon is accepted at pharmacies nationwide.</strong>
              </p>
              <p style={{ margin: 0 }}>
                Prices may vary by pharmacy. Look up your medication close to when you plan to fill the prescription.
              </p>
            </div>
          </div>
        </div>

        {/* Accepted Pharmacies - Hidden on mobile for cleaner look */}
        {!isMobile && (
          <div style={{
            padding: '20px 32px 32px',
            borderTop: `1px solid ${colors.gray100}`
          }}>
            <p style={{ fontSize: 13, color: colors.gray500, margin: '0 0 12px', textAlign: 'center' }}>
              Accepted at major pharmacies including:
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', opacity: 0.6 }}>
              {['CVS', 'Walgreens', 'Walmart', 'Rite Aid', 'Kroger', 'Safeway', 'Target', 'Costco'].map(p => (
                <span key={p} style={{ fontSize: 14, color: colors.gray600, fontWeight: 600 }}>{p}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media print {
          body * { visibility: hidden; }
          #coupon-card, #coupon-card * { visibility: visible; }
          #coupon-card { position: absolute; left: 0; top: 0; }
        }
      `}</style>
    </div>
  );
};

export default CouponModal;